import { flagVariant, type FlagVariant, type MarkStyle } from '@/content/marks'
import Wave from './Wave'


type FlagProps = {
  /** A flag drawing, or a mark style: flag, seal and logo all show the flag. */
  variant: FlagVariant | MarkStyle
  className?: string
  /** Accessible name; omit when the flag sits beside visible text. */
  title?: string
}

// The flag's own colors. The official and motion variants keep them in every
// theme; the simplified variants take the surrounding ink or theme roles.
const OLD_GLORY_RED = '#b31942'
const OLD_GLORY_BLUE = '#0a3161'

const W = 190
const H = 100
const STRIPE = H / 13
const CANTON_W = 76
const CANTON_H = STRIPE * 7

function starPoints(cx: number, cy: number, r: number) {
  return Array.from({ length: 10 }, (_, i) => {
    const a = ((-90 + i * 36) * Math.PI) / 180
    const radius = i % 2 === 0 ? r : r * 0.382
    return `${(cx + radius * Math.cos(a)).toFixed(2)},${(cy + radius * Math.sin(a)).toFixed(2)}`
  }).join(' ')
}

const stars = Array.from({ length: 9 }, (_, row) => {
  const count = row % 2 === 0 ? 6 : 5
  const offset = row % 2 === 0 ? 6.33 : 12.66
  return Array.from({ length: count }, (_, col) => [offset + col * 12.66, 5.385 + row * 5.385])
}).flat()

export function OfficialArt() {
  return (
    <>
      <rect width={W} height={H} fill="#fff" />
      {Array.from({ length: 7 }, (_, i) => (
        <rect key={i} y={i * 2 * STRIPE} width={W} height={STRIPE} fill={OLD_GLORY_RED} />
      ))}
      <rect width={CANTON_W} height={CANTON_H} fill={OLD_GLORY_BLUE} />
      {stars.map(([x, y], i) => (
        <polygon key={i} points={starPoints(x, y, 3.08)} fill="#fff" />
      ))}
    </>
  )
}

function Official({ label }: { label?: string }) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role={label ? 'img' : undefined} aria-hidden={label ? undefined : true}>
      {label && <title>{label}</title>}
      <OfficialArt />
    </svg>
  )
}

/** A simple flag: five stripes and an open blue canton, readable at any size. */
function Simple({ label }: { label?: string }) {
  return (
    <svg viewBox="0 0 34 20" role={label ? 'img' : undefined} aria-hidden={label ? undefined : true}>
      {label && <title>{label}</title>}
      <rect width="34" height="20" rx="2" fill="#fff" />
      <path d="M2 0h30a2 2 0 0 1 2 2v2H0V2a2 2 0 0 1 2-2Z" fill="#d80007" />
      <rect y="8" width="34" height="4" fill="#d80007" />
      <path d="M0 16h34v2a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2Z" fill="#d80007" />
      <path d="M2 0h13v12H0V2a2 2 0 0 1 2-2Z" fill="#004ac3" />
    </svg>
  )
}

/** An 8-bit flag: 19 by 10 cells, stars on a checker in the canton. */
function Pixel({ label }: { label?: string }) {
  const cells: Array<[number, number, string]> = []
  for (let y = 0; y < 10; y += 1)
    for (let x = 0; x < 19; x += 1) {
      if (x < 8 && y < 5) cells.push([x, y, (x + y) % 2 === 0 ? 'var(--mds-flag-blue)' : '#fff'])
      else cells.push([x, y, y % 2 === 0 ? 'var(--mds-flag-red)' : '#fff'])
    }
  return (
    <svg viewBox="0 0 19 10" shapeRendering="crispEdges" role={label ? 'img' : undefined} aria-hidden={label ? undefined : true}>
      {label && <title>{label}</title>}
      {cells.map(([x, y, fill]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={1.02} height={1.02} fill={fill} />
      ))}
    </svg>
  )
}

/** The official flag, waving. Still under reduced motion. */
function Motion({ label }: { label?: string }) {
  return (
    <Wave width={W} height={H} label={label}>
      <OfficialArt />
    </Wave>
  )
}

export default function Flag({ variant: requested, className, title }: FlagProps) {
  const variant = flagVariant(requested)
  const Art = { official: Official, simple: Simple, pixel: Pixel, motion: Motion }[variant]
  return (
    <span className={`mds-flag mds-flag--${variant}${className ? ` ${className}` : ''}`}>
      <Art label={title} />
    </span>
  )
}
